/**
 * hooks/useContentBuffer.ts
 * 
 * Buffer management hook for chunked content.
 * Handles section preloading, memory management, and seamless transitions.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  ContentSection,
  BufferConfig,
  UseContentBufferOptions,
  UseContentBufferReturn,
  DEFAULT_BUFFER_CONFIG,
} from '../types/content';

export function useContentBuffer({
  sections,
  initialSectionIndex = 0,
  initialWordIndex = 0,
  bufferConfig = DEFAULT_BUFFER_CONFIG,
}: UseContentBufferOptions): UseContentBufferReturn {
  // Current section tracking
  const [currentSectionIndex, setCurrentSectionIndex] = useState(initialSectionIndex);
  const [currentWordIndex, setCurrentWordIndex] = useState(initialWordIndex);
  
  // Preloading state
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [isPreloading, setIsPreloading] = useState(false);
  
  // Memory management: track loaded sections
  const [loadedSections, setLoadedSections] = useState<Set<number>>(new Set([initialSectionIndex]));
  
  // Refs
  const preloadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Current and next sections (memoized)
  const currentSection = useMemo(() => {
    if (sections.length === 0) return null;
    return sections[currentSectionIndex] || null;
  }, [sections, currentSectionIndex]);
  
  const nextSection = useMemo(() => {
    if (sections.length === 0 || currentSectionIndex >= sections.length - 1) return null;
    return sections[currentSectionIndex + 1] || null;
  }, [sections, currentSectionIndex]);
  
  // Check if we should preload the next section
  const shouldPreloadNext = useCallback(() => {
    if (!bufferConfig.enabled || !bufferConfig.preloadNextSection) return false;
    if (!currentSection) return false;
    
    const wordsRemaining = currentSection.wordCount - currentWordIndex;
    return wordsRemaining <= bufferConfig.preloadWordCount;
  }, [bufferConfig, currentSection, currentWordIndex]);
  
  // Preload next section
  const preloadNext = useCallback(() => {
    if (!nextSection || !bufferConfig.enabled) return;
    
    const nextIndex = currentSectionIndex + 1;
    if (loadedSections.has(nextIndex)) return;
    
    setIsPreloading(true);
    
    // Simulate async loading (in real implementation, this could fetch from network)
    setTimeout(() => {
      setLoadedSections((prev) => new Set([...prev, nextIndex]));
      setIsPreloading(false);
      setPreloadProgress(100);
    }, 100);
  }, [nextSection, bufferConfig.enabled, currentSectionIndex, loadedSections]);
  
  // Check for preloading trigger
  useEffect(() => {
    if (!bufferConfig.enabled) return;
    
    if (shouldPreloadNext()) {
      // Clear any existing timeout
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current);
      }
      
      // Debounce preloading
      preloadTimeoutRef.current = setTimeout(() => {
        preloadNext();
      }, 50);
    }
    
    return () => {
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current);
      }
    };
  }, [shouldPreloadNext, preloadNext, bufferConfig.enabled]);
  
  // Update preload progress based on position in current section
  useEffect(() => {
    if (!currentSection || !bufferConfig.enabled) {
      setPreloadProgress(0);
      return;
    }
    
    const progress = (currentWordIndex / currentSection.wordCount) * 100;
    setPreloadProgress(Math.min(progress, 100));
  }, [currentWordIndex, currentSection, bufferConfig.enabled]);
  
  // Memory management: evict old sections when exceeding max
  useEffect(() => {
    if (!bufferConfig.enabled) return;
    if (loadedSections.size <= bufferConfig.maxSectionsInMemory) return;
    
    // Keep current, previous, and next sections
    const sectionsToKeep = new Set<number>([
      currentSectionIndex,
      currentSectionIndex - 1, // previous
      currentSectionIndex + 1, // next
    ].filter((idx) => idx >= 0 && idx < sections.length));
    
    // Find sections to evict
    const sectionsToEvict = [...loadedSections].filter(
      (idx) => !sectionsToKeep.has(idx)
    );
    
    if (sectionsToEvict.length > 0) {
      setLoadedSections((prev) => {
        const newSet = new Set(prev);
        sectionsToEvict.forEach((idx) => newSet.delete(idx));
        return newSet;
      });
    }
  }, [loadedSections.size, currentSectionIndex, sections.length, bufferConfig]);
  
  // Jump to a specific section
  const jumpToSection = useCallback((index: number) => {
    if (index < 0 || index >= sections.length) return;
    
    setCurrentSectionIndex(index);
    setCurrentWordIndex(0);
    setLoadedSections((prev) => new Set([...prev, index]));
  }, [sections.length]);
  
  // Update current word index (called from RSVP reader)
  const setCurrentWordIndexHandler = useCallback((index: number) => {
    if (!currentSection) return;
    
    // Check if we need to transition to next section
    if (index >= currentSection.wordCount && currentSectionIndex < sections.length - 1) {
      setCurrentSectionIndex((prev) => prev + 1);
      setCurrentWordIndex(0);
    } else {
      setCurrentWordIndex(index);
    }
  }, [currentSection, currentSectionIndex, sections.length]);
  
  return {
    currentSection,
    nextSection,
    currentSectionIndex,
    preloadProgress,
    isPreloading,
    jumpToSection,
    setCurrentWordIndex: setCurrentWordIndexHandler,
  };
}

export default useContentBuffer;
